/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  getChannelEntryId,
} from './helpers/channelHelpers';
import { findListingEntity } from './helpers/entityReadHelpers';
import { upsertListing, upsertListingVariant } from './helpers/entityAdapter';
import { setActiveTabByProductChannel } from './helpers/uiAdapter';
import { showChannelGoneToastFromItems } from './helpers/showChannelProductGoneToast';

export const refreshListingVariantByMaster = createAsyncThunk(
  'products/refreshListingVariantByMaster',
  async ({ masterProductId, masterVariantId }, { extra: { Logger, Toast, sdk } }) => {
    try {
      const { data: variants } = await sdk.products.getVariantsByMasterVariantId(masterVariantId);

      showChannelGoneToastFromItems({ Toast, items: variants });

      return {
        masterProductId,
        masterVariantId,
        variants: variants || [],
      };
    } catch (error) {
      Logger.error(new Error('Error refreshing listing variant by master'), { error }, [
        'RefreshListingVariantByMasterFailed',
        { errorMessage: 'Cannot refresh listing variant', masterVariantId },
      ]);
      throw error;
    }
  },
);

const upsertLiveVariantEntry = (listing, listingVariant) => {
  if (!listing?.live?.variants) return listing.live;

  const liveVariants = [...(listing.live.variants || [])];
  const idx = liveVariants.findIndex(
    (variant) => variant.masterVariantId === listingVariant.masterVariantId,
  );

  if (listingVariant.live) {
    const entry = {
      ...listingVariant.live,
      _id: listingVariant._id,
      masterVariantId: listingVariant.masterVariantId,
      title: listingVariant.live.title || listingVariant.title,
    };

    if (idx >= 0) {
      liveVariants[idx] = entry;
    } else {
      liveVariants.push(entry);
    }
  } else if (idx >= 0) {
    liveVariants.splice(idx, 1);
  }

  return {
    ...listing.live,
    variants: liveVariants,
  };
};

export const reducer = (builder) => {
  builder.addCase(refreshListingVariantByMaster.fulfilled, (state, { payload }) => {
    const { masterProductId, variants } = payload;

    variants.forEach((listingVariant) => {
      const channelId = getChannelEntryId({ salesChannelId: listingVariant.salesChannelId });
      if (!channelId) return;

      // Drop ephemeral API-only flags — toast already handled in the thunk.
      const variantWithoutGoneFlag = { ...listingVariant };
      delete variantWithoutGoneFlag.channelProductGone;
      delete variantWithoutGoneFlag.channelVariantGone;

      const existingListing = findListingEntity(state, { masterProductId, channelId })
        || {
          _id: variantWithoutGoneFlag.baseProductId,
          masterProductId,
          salesChannelId: variantWithoutGoneFlag.salesChannelId,
          salesChannel: variantWithoutGoneFlag.salesChannel,
          listingStatus: variantWithoutGoneFlag.listingStatus,
        };

      const activeTab = state.ui?.activeTabByProductChannel?.[masterProductId]?.[channelId]
        || existingListing.listingStatus
        || variantWithoutGoneFlag.listingStatus;

      setActiveTabByProductChannel(state, masterProductId, channelId, activeTab);
      upsertListingVariant(state, variantWithoutGoneFlag);

      const listingAssets = state.entities?.assets?.listingAssets?.[existingListing._id];
      const nextListing = {
        ...existingListing,
        _id: existingListing._id || variantWithoutGoneFlag.baseProductId,
        masterProductId,
        salesChannelId: variantWithoutGoneFlag.salesChannelId || existingListing.salesChannelId,
        salesChannel: variantWithoutGoneFlag.salesChannel || existingListing.salesChannel,
        listingStatus: variantWithoutGoneFlag.listingStatus || existingListing.listingStatus,
        ...(listingAssets !== undefined && { assets: listingAssets }),
        live: upsertLiveVariantEntry(existingListing, variantWithoutGoneFlag),
      };

      if (nextListing._id) {
        upsertListing(state, nextListing);
      }
    });
  });
};
